import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { HTTPError } from "ky";
import type { Control } from "react-hook-form";
import type { ColumnDef } from "@tanstack/react-table";
import type { Credential, CredentialFormData } from "@/lib/queries/credentials";
import type * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { GenericTable } from "@/components/generic-table";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  CREDENTIAL_TYPES_DISPLAY,
  credentialFormSchema,
  credentialsListQueryOptions,
  useCredentialsList,
} from "@/lib/queries/credentials";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/ui/passwordInput";
import { api } from "@/lib/http";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";

export const Route = createFileRoute("/_app/credentials/")({
  component: RouteComponent,

  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(credentialsListQueryOptions());
  },
  staticData: {
    permissionGuards: [
      {
        resource: "credentials",
      },
    ],
  },
});

const columns: Array<ColumnDef<Credential>> = [
  { header: "Type", accessorKey: "type", size: 400 },
  {
    header: "Created",
    accessorKey: "created_at",
    size: 400,
    cell({ getValue }) {
      const v = getValue<string | undefined>();
      return v ? new Date(v).toLocaleDateString() : "—";
    },
  },
  {
    header: "Actions",
    id: "actions",
    maxSize: 150,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canEdit = useResourceAccessPermission("credentials", ["update"]);
      const canDelete = useResourceAccessPermission("credentials", ["delete"]);

      return (
        <Actions>
          {canEdit.data && (
            <EditDialog asChild credential={original}>
              <Button
                size="lg"
                variant="ghost"
                className="w-full justify-start text-base"
              >
                <PencilIcon className="size-5" />
                Edit
              </Button>
            </EditDialog>
          )}
          {canDelete.data && <DeleteDialog credential={original} />}
        </Actions>
      );
    },
  },
];

function RouteComponent() {
  const credentials = useCredentialsList();

  const canEdit = useResourceAccessPermission("credentials", ["update"]);
  const canDelete = useResourceAccessPermission("credentials", ["delete"]);
  const canCreate = useResourceAccessPermission("credentials", ["create"]);

  const showActions = Boolean(canEdit.data || canDelete.data);

  const table = useReactTable({
    data: credentials.data ?? [],
    columns,
    filterFns: { fuzzy: fuzzyFilter },
    state: {
      columnVisibility: {
        actions: showActions,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Credentials</h1>
        {canCreate.data && (
          <CreateDialog asChild>
            <Button>
              <PlusIcon />
              Create Credential
            </Button>
          </CreateDialog>
        )}
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={credentials.isLoading} />
      </div>
    </div>
  );
}

function DeleteDialog({ credential }: { credential: Credential }) {
  const [open, setOpen] = React.useState(false);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: api.credentials.remove,
    onSuccess() {
      qc.invalidateQueries({ queryKey: ["credentials", "list"] });
      qc.removeQueries({ queryKey: ["credentials", "detail", credential.id] });

      setOpen(false);
      toast.success(`Credential "${credential.type}" deleted successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body.detail) message = body.detail;
      }
      toast.error(`Update failed: ${message}`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Credential</AlertDialogTitle>
          <AlertDialogDescription>
            Delete credential "{credential.type}"? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(credential.id)}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  children,
  credential,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; credential: Credential }>) {
  const [open, setOpen] = React.useState(false);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CredentialFormData }) =>
      api.credentials.update(id, data),
    onSuccess(data) {
      qc.invalidateQueries({ queryKey: ["credentials", "list"] });
      qc.setQueryData(["credentials", "detail", credential.id], data);

      setOpen(false);
      toast.success(`Credential ${data.type} updated successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body.detail) message = body.detail;
      }
      toast.error(`Update failed: ${message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Credential</DialogTitle>
          <DialogDescription>Update type and config.</DialogDescription>
        </DialogHeader>
        <CredentialForm
          hideType
          defaultValue={{ type: credential.type }}
          disabled={mutation.isPending}
          buttonText={mutation.isPending ? "Saving..." : "Update"}
          onSubmit={(data) => mutation.mutate({ id: credential.id, data })}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: api.credentials.create,
    onSuccess(data) {
      qc.invalidateQueries({ queryKey: ["credentials", "list"] });
      qc.setQueryData(["credentials", "detail", data.id], data);

      setOpen(false);
      toast.success(`Credential created successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body.detail) message = body.detail;
      }
      toast.error(`Create failed: ${message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Credential</DialogTitle>
          <DialogDescription>
            Select a type and provide config.
          </DialogDescription>
        </DialogHeader>
        <CredentialForm
          disabled={mutation.isPending}
          buttonText={mutation.isPending ? "Saving..." : "Create"}
          onSubmit={(d) => mutation.mutate(d)}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function CredentialForm({
  hideType = false,
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
}: {
  disabled?: boolean;
  defaultValue?: Partial<CredentialFormData>;
  buttonText: React.ReactNode;
  onSubmit: (data: CredentialFormData) => void;
  onCancel?: () => void;
  hideType?: boolean;
}) {
  const form = useForm({
    resolver: zodResolver(credentialFormSchema),
    defaultValues: {
      type: defaultValue?.type ?? "OPENAI",
      // giving up on the rest because discriminated union stuff, plus it has to be empty anyway
    },
  });

  const type = form.watch("type", "OPENAI");
  const ConfigForm = TYPE_FORM_MAP[type];
  React.useEffect(() => {
    form.resetField("config");
    if (type === "EMAIL_CONFIG")
      form.setValue("config.port", 587, {
        shouldDirty: false,
        shouldTouch: false,
      });
  }, [type]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className={hideType ? "hidden" : undefined}>
              <FormLabel>Type</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(CREDENTIAL_TYPES_DISPLAY).map(([k, v]) => (
                    <SelectItem value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <ConfigForm control={form.control} />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}

type CredentialConfigControl = Control<
  z.input<typeof credentialFormSchema>,
  unknown,
  CredentialFormData
>;
const TYPE_FORM_MAP = {
  EMAIL_CONFIG: EmailConfigForm,
  SLACK: SlackConfigForm,
  OPENAI: OpenaiConfigForm,
  GEMINI: OpenaiConfigForm,
  DEEPSEEK: OpenaiConfigForm,
} as const satisfies Record<
  CredentialFormData["type"],
  (props: { control: CredentialConfigControl }) => React.ReactNode
>;

function EmailConfigForm({ control }: { control: CredentialConfigControl }) {
  return (
    <>
      <FormField
        name="config.username"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Username</FormLabel>
            <FormControl>
              <Input {...field} placeholder="SMTP username" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="config.password"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Password</FormLabel>
            <FormControl>
              <PasswordInput {...field} placeholder="SMTP password" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="config.server"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Server</FormLabel>
            <FormControl>
              <Input {...field} placeholder="smtp.example.com" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="config.port"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Port</FormLabel>
            <FormControl>
              <Input
                {...field}
                onChange={(e) => field.onChange(e.target.valueAsNumber)}
                type="number"
                placeholder="587"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="config.sender"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sender Email</FormLabel>
            <FormControl>
              <Input
                {...field}
                type="email"
                placeholder="noreply@example.com"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-2 gap-4">
        <FormField
          name="config.starttls"
          control={control}
          render={({ field: { value, ...field } }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel className="text-sm font-medium">STARTTLS</FormLabel>
              </div>
              <FormControl>
                <Checkbox
                  {...field}
                  checked={value}
                  onCheckedChange={field.onChange}
                  aria-label="Enable STARTTLS"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          name="config.ssl_tls"
          control={control}
          render={({ field: { value, ...field } }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel className="text-sm font-medium">SSL/TLS</FormLabel>
              </div>
              <FormControl>
                <Checkbox
                  {...field}
                  checked={value}
                  onCheckedChange={field.onChange}
                  aria-label="Enable SSL/TLS"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </>
  );
}

function SlackConfigForm({ control }: { control: CredentialConfigControl }) {
  return (
    <FormField
      name="config.token"
      control={control}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Bot Token</FormLabel>
          <FormControl>
            <PasswordInput {...field} placeholder="xoxb-..." />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function OpenaiConfigForm({ control }: { control: CredentialConfigControl }) {
  return (
    <FormField
      name="config.api_key"
      control={control}
      render={({ field }) => (
        <FormItem>
          <FormLabel>API Key</FormLabel>
          <FormControl>
            <PasswordInput {...field} placeholder="sk-..." />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
